import React from 'react';
import { Box, Flex, SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react';
import OptionComponent from './OptionComponent';
import ButtonComponent from './ButtonComponent';
import SpacerComponent from "./SpacerComponent";
import ListComponent from './ListComponent';
import { areRequiredFieldsValid } from '../Utils/utils';
import DOMPurify from 'dompurify';

const QuestionnaireComponent = ({
  question,
  formData,
  readOnly,
  priorDecisions = {},
  updateFormData,
  handleButtonAction,
  onOptionSelect,
  isTouchDevice,
  isMouseDevice,
  isFollowUp = false,
  isDynamicQuestion = false
}) => {
  // Get the current formData entry for this question - this is our source of truth
  const currentFormEntry = React.useMemo(() => {
    return formData[question.id];
  }, [formData, question.id]);

  const currentFormValue = currentFormEntry?.value;
  const currentFormLabel = currentFormEntry?.label;

  // Derive selected values from formData, filtering out empty strings that can
  // appear when clearFollowUpAnswers resets a multi-choice field to '' on re-selection

  const valueCounts = React.useMemo(() => {
    const counts = new Map();
    (question.options || []).forEach((option) => {
      const key = String(option.value);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [question.options]);

  // Helper function to check if an option is selected based on formData (not option.isSelected)
  const isOptionSelected = React.useCallback((option) => {
    const optionValue = option?.value;
    if (currentFormValue === undefined || currentFormValue === null) {
      return false;
    }
    
    if (Array.isArray(currentFormValue)) {
      return currentFormValue.includes(optionValue);
    } else {
      if (currentFormValue != optionValue) {
        return false;
      }

      const hasDuplicateValue = (valueCounts.get(String(optionValue)) || 0) > 1;
      if (question.choice === 'single' && hasDuplicateValue && currentFormLabel) {
        return currentFormLabel === option.label;
      }

      return true;
    }
  }, [currentFormValue, currentFormLabel, question.choice, valueCounts]);

  // Initialize formData from server state if needed (only when component first mounts)
  React.useEffect(() => {
     if (!question.options) {
      console.warn('[QuestionnaireComponent] question.options is undefined for question:', { id: question.id, type: question.type, view: question.view });
    }
    // If no formData exists but server has selected options, update formData
    const selectedOptions = (question.options || []).filter(option => 
      option.isSelected === 1 && (question.showAllOptions || option.showAllOptions ? true : option.isVisible !== 'false')
    );
    
    if (selectedOptions.length > 0 && !currentFormValue) {
      if (question.choice === 'multiple') {
        // Multiple choice - update formData with array of values
        const selectedValues = selectedOptions.map(option => option.value);
        const labelString = selectedOptions.map(option => option.label).join(', ');
        updateFormData(
          question.id,
          selectedValues,
          labelString,
          question.metric,
          question.flowSheetId
        );
      } else {
        // Single choice - update formData with single value
        const selectedOption = selectedOptions[0];
        updateFormData(
          question.id,
          selectedOption.value,
          selectedOption.label,
          question.metric,
          question.flowSheetId
        );
      }
    } else if (question.view === 'input' || question.view === 'input-grid') {
      // Handle input fields with default values
      const inputOptions = question.options || [];
      const hasDefaultValues = inputOptions.some(option => 
        option.value !== null && option.value !== undefined && option.value !== ''
      );
      
      if (hasDefaultValues && !currentFormValue) {
        // Create input answers string from default values
        const inputAnswers = inputOptions
          .filter(opt => {
            const v = opt.value;
            if (v === null || v === undefined) return false;
            if (typeof v === 'number') return true; // include numeric values like 0, 5, 10
            const s = String(v);
            return s.trim() !== '';
          })
          .map(opt => `${opt.label}: ${opt.value}`)
          .join(', ');
        
        // Update formData with default values
        updateFormData(
          question.id,
          inputOptions,
          inputAnswers,
          question.metric,
          question.flowSheetId
        );
      }
    }
  }, [question.id]); // Only run when question changes, not formData

  // Get selected values from formData (this is the source of truth)
  const selectedValue = React.useMemo(() => {
    if (currentFormValue === undefined || currentFormValue === null || currentFormValue === '') {
      return [];
    }
    
    if (Array.isArray(currentFormValue)) {
      // Filter out empty strings left behind by clearFollowUpAnswers resets
      return currentFormValue.filter(v => v !== '' && v !== null && v !== undefined);
    } else {
      return [currentFormValue];
    }
  }, [currentFormValue]);

  const handleChange = (value, isCheckbox = false, optionId = null, selectedOptionOverride = null) => {
    if (isCheckbox) {
      // Multiple choice logic
      const currentValues = [...selectedValue];
      const valueAlreadySelected = currentValues.includes(value);
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      
      const labelString = updatedValues
        .map((val) => question.options.find((opt) => opt.value === val)?.label)
        .filter(Boolean)
        .join(', ');
      
      updateFormData(
        question.id,
        updatedValues,
        labelString,
        question.metric,
        question.flowSheetId
      );
    } else {
      // Handle input fields
      if (question.view === 'input' || question.view === 'input-grid') {
        // For input fields, update the specific option's value
        const currentInputData = formData[question.id]?.value || question.options || [];
        const updatedOptions = Array.isArray(currentInputData) 
          ? currentInputData.map(opt => {
              if (opt.id === optionId) {
                // Ensure we only store clean, serializable data
                return { 
                  id: opt.id,
                  label: opt.label,
                  value: value,
                  inputType: opt.inputType,
                  min: opt.min,
                  max: opt.max,
                  required: opt.required,
                  placeholder: opt.placeholder
                };
              }
              return {
                id: opt.id,
                label: opt.label,
                value: opt.value,
                inputType: opt.inputType,
                min: opt.min,
                max: opt.max,
                required: opt.required,
                placeholder: opt.placeholder
              };
            })
          : question.options.map(opt => {
              if (opt.id === optionId) {
                // Ensure we only store clean, serializable data
                return { 
                  id: opt.id,
                  label: opt.label,
                  value: value,
                  inputType: opt.inputType,
                  min: opt.min,
                  max: opt.max,
                  required: opt.required,
                  placeholder: opt.placeholder
                };
              }
              return {
                id: opt.id,
                label: opt.label,
                value: opt.value,
                inputType: opt.inputType,
                min: opt.min,
                max: opt.max,
                required: opt.required,
                placeholder: opt.placeholder
              };
            });
        
        // Create a combined answer string from all input values
        const inputAnswers = updatedOptions
          .filter(opt => {
            const v = opt.value;
            if (v === null || v === undefined) return false;
            if (typeof v === 'number') return true; // include numeric values like 0, 5, 10
            const s = String(v);
            return s.trim() !== '';
          })
          .map(opt => `${opt.label}: ${opt.value}`)
          .join(', ');
        
        updateFormData(
          question.id,
          updatedOptions,
          inputAnswers,
          question.metric,
          question.flowSheetId
        );
      } else {
        // Single choice logic
        const selectedOption = selectedOptionOverride || question.options.find(
          (opt) => opt.value === value
        );
        
        updateFormData(
          question.id,
          value,
          selectedOption?.label || '',
          question.metric,
          question.flowSheetId
        );

        if (onOptionSelect) {
          onOptionSelect(selectedOption);
        }
      }
    }
  };

  const isOptionDisabled = (value) =>
    Object.values(priorDecisions).includes(value);
  

  
  // Select all functionality
  const visibleOptions = (question.options || []).filter(option => 
    (question.showAllOptions || option.showAllOptions) ? true : option.isVisible !== "false"
  );
  const allOptionValues = visibleOptions.map(option => option.value);
  const areAllVisibleOptionsSelected = allOptionValues.every(value => 
    selectedValue.includes(value)
  );

  const handleSelectAll = () => {
    if (areAllVisibleOptionsSelected) {
      // If all are selected, deselect all
      updateFormData(
        question.id,
        [],
        '',
        question.metric,
        question.flowSheetId
      );
    } else {
      // If not all are selected, select all
      updateFormData(
        question.id,
        allOptionValues,
        visibleOptions.map(option => option.label).join(', '),
        question.metric,
        question.flowSheetId
      );
    }
  };

  // Check if buttons should be disabled based on required fields
  // "No Thanks" buttons should always be enabled
  // "Add" buttons should only be enabled when selections are made
  const hasRequiredFields = question.required && question.required.length > 0;
  let isButtonDisabled = false;
  
  if (hasRequiredFields) {
    // Use the original areRequiredFieldsValid function but handle the case where fields don't exist yet
    const allRequiredFieldsValid = question.required.every((fieldId) => {
      const fieldData = formData[fieldId];
      
      // If field doesn't exist yet, consider it invalid (will disable buttons)
      if (!fieldData) {
        return false;
      }
      
      // Use the original validation logic
      const value = fieldData.value;
      
      // Handle arrays (multiple choice and input fields)
      if (Array.isArray(value)) {
        // For input fields, check if any option has a non-empty value
        if (value.length > 0 && typeof value[0] === 'object' && value[0].hasOwnProperty('value')) {
          // This is an input field array - check if any input has a value
          return value.some(option => {
            const inputValue = option.value;
            if (inputValue === null || inputValue === undefined) return false;
            if (typeof inputValue === 'number') return true; // include numeric values like 0, 5, 10
            const stringValue = String(inputValue);
            return stringValue.trim() !== '';
          });
        }
        // Regular multiple choice array
        // Optional stricter mode: require all visible options to be selected.
        const shouldRequireAllSelections = question.requiredMode === 'all' && fieldId === question.id;
        if (shouldRequireAllSelections) {
          const requiredOptions = (question.options || []).filter(option =>
            (question.showAllOptions || option.showAllOptions) ? true : option.isVisible !== 'false'
          );

          if (requiredOptions.length === 0) {
            return false;
          }

          return requiredOptions.every(option => value.includes(option.value));
        }

        return value.length > 0;
      }
      
      // Handle single values
      return value !== null && value !== '';
    });
    
    isButtonDisabled = !allRequiredFieldsValid;
  } else {
    // If no required fields are specified, check if current question has input values
    // This handles cases where the question itself has input fields that need validation
    if (question.view === 'input' || question.view === 'input-grid') {
      const currentInputData = formData[question.id]?.value || (question.options || []);
      
      if (Array.isArray(currentInputData) && currentInputData.length > 0) {
        // Check if any input field has a value
        const hasInputValues = currentInputData.some(option => {
          if (typeof option === 'object' && option.hasOwnProperty('value')) {
            const inputValue = option.value;
            if (inputValue === null || inputValue === undefined) return false;
            if (typeof inputValue === 'number') return true; // include numeric values like 0, 5, 10
            const stringValue = String(inputValue);
            return stringValue.trim() !== '';
          }
          return false;
        });
        
        // If we have input fields but no values, disable the button
        isButtonDisabled = !hasInputValues;
      } else {
        // No input data yet, disable the button
        isButtonDisabled = true;
      }
    }
  }
  
  // Final override: If this is an input question and we have values, ensure button is enabled
  // This handles cases where required fields validation might be failing for input fields
  if (question.view === 'input' || question.view === 'input-grid') {
    const currentInputData = formData[question.id]?.value || (question.options || []);
    
    if (Array.isArray(currentInputData) && currentInputData.length > 0) {
      // Check if any input field has a value
      const hasInputValues = currentInputData.some(option => {
        if (typeof option === 'object' && option.hasOwnProperty('value')) {
          const inputValue = option.value;
          if (inputValue === null || inputValue === undefined) return false;
          if (typeof inputValue === 'number') return true; // include numeric values like 0, 5, 10
          const stringValue = String(inputValue);
          return stringValue.trim() !== '';
        }
        return false;
      });
      
      // If we have input values, override any previous disabled state
      if (hasInputValues) {
        isButtonDisabled = false;
      }
    }
  }
  

  const renderChoiceGroup = () => {
    if (question.view === 'list') {
      return (
                 <Stack>
                      <Stack direction="column" spacing={4}>
              {(question.options || [])
                .filter(option => (question.showAllOptions || option.showAllOptions) ? true : option.isVisible !== "false")
                .map((option, index) => (
                                 <OptionComponent
                   key={index}
                   option={option}
                                   isSelected={isOptionSelected(option)}
                                   onSelect={() => handleChange(option.value, question.choice === 'multiple', null, option)}
                   view={question.view}
                   choice={question.choice}
                   isDisabled={isOptionDisabled(option.value)}
                   variant={option.variant || 'body-sm-min-reg'}
                   ariaLabel={option.ariaLabel || `Option: ${option.label}`}
                 />
              ))}
          </Stack>
        </Stack>
      );
    }

    if (['grid', 'box'].includes(question.view)) {
      return (
                                   <SimpleGrid
            columns={question.columns || 2}
            spacing={question.spacing || 2}
          >
            {(question.options || [])
              .filter(option => (question.showAllOptions || option.showAllOptions) ? true : option.isVisible !== "false")
              .map((option, index) => (
              <OptionComponent
                key={index}
                option={option}
                isSelected={isOptionSelected(option)}
                onSelect={() =>
                  handleChange(option.value, question.choice === 'multiple', null, option)
                }
                view={question.view}
                choice={question.choice}
                isDisabled={isOptionDisabled(option.value)}
                variant={option.variant || 'body-sm-min-reg'}
                iconVariant={option.iconVariant}
                ariaLabel={option.ariaLabel || `Option: ${option.label}`}
              />
            ))}
        </SimpleGrid>
      );
    }

    if (question.view === 'card') {
                                                       return (
           <Stack 
           mt={question.optionsMarginTop || 0}
           mb={question.optionsMarginBottom || question.marginBottom || 0} 
           spacing={question.spacing || 4}>
             {(question.options || [])
               .filter(option => (question.showAllOptions || option.showAllOptions) ? true : option.isVisible !== "false")
               .map((option, index) => (
               <OptionComponent
                 key={index}
                 option={option}
                isSelected={isOptionSelected(option)}
                 onSelect={() =>
                  handleChange(option.value, question.choice === 'multiple', null, option)
                 }
                 view={question.view}
                 choice={question.choice}
                 isDisabled={isOptionDisabled(option.value)}
                variant={option.variant || 'body-sm-min-reg'}
                iconVariant={option.iconVariant}
                ariaLabel={option.ariaLabel || `Option: ${option.label}`}
              />
            ))}
        </Stack>
      );
    }

    if (question.view === 'flow') {
    return (
           <Box display="flex" flexWrap="wrap" gap={ question.optionsGap ? question.optionsGap : 4} justifyContent={question.optionsJustifyContent ? question.optionsJustifyContent : '' }>
             {(question.options || [])
               .filter(option => (question.showAllOptions || option.showAllOptions) ? true : option.isVisible !== "false")
               .map((option, index) => (
               <OptionComponent
                 key={index}
                 option={option}
                isSelected={isOptionSelected(option)}
                 onSelect={() =>
                  handleChange(option.value, question.choice === 'multiple', null, option)
                 }
                 view={question.view}
                 choice={question.choice}
                 isDisabled={isOptionDisabled(option.value)}
                variant={option.variant || 'body-sm-min-reg'}
                iconVariant={option.iconVariant}
                ariaLabel={option.ariaLabel || `Option: ${option.label}`}
              />
            ))}
        </Box>
      );
    }

    // Input grid view
    if (question.view === 'input-grid') {
      const currentInputData = formData[question.id]?.value || (question.options || []);
      
      return (
        <VStack spacing={4} align="stretch" width="100%" flex="1" justify="space-between">
          <SimpleGrid
            columns={question.columns || 2}
            spacing={question.spacing || 4}
            mb={question.optionsMarginBottom || 0}
          >
            {(question.options || [])
              .filter(option => (question.showAllOptions || option.showAllOptions) ? true : option.isVisible !== "false")
              .map((option, index) => {
              const currentOption = Array.isArray(currentInputData) 
                ? currentInputData.find(opt => opt.id === option.id) || option
                : option;
              
              return (
                <OptionComponent
                  key={index}
                  option={{
                    ...option,
                    value: currentOption.value || ''
                  }}
                  isSelected={false}
                  onSelect={(value) => {
                    handleChange(value, false, option.id);
                  }}
                  view="input-grid"
                  choice={question.choice}
                  isDisabled={isOptionDisabled(option.value)}
                  variant={option.variant || 'body-sm-min-reg'}
                  ariaLabel={option.ariaLabel || `Input field: ${option.label}`}
                />
              );
            })}
          </SimpleGrid>
          {/* Spacer to push input to top and fill remaining space */}
          <Box flex="1" />
        </VStack>
      );
    }

    // Input view
    if (question.view === 'input') {
      const currentInputData = formData[question.id]?.value || (question.options || []);
      
      return (
        <VStack spacing={4} align="stretch" width="100%" flex="1" justify="space-between" mb={question.optionsMarginBottom || 0}>
            {(question.options || [])
              .filter(option => (question.showAllOptions || option.showAllOptions) ? true : option.isVisible !== "false")
              .map((option, index) => {
              const currentOption = Array.isArray(currentInputData) 
                ? currentInputData.find(opt => opt.id === option.id) || option
                : option;
              
              return (
                <OptionComponent
                  key={index}
                  option={{
                    ...option,
                    value: currentOption.value || ''
                  }}
                  isSelected={false}
                  onSelect={(value) => {
                    handleChange(value, false, option.id);
                  }}
                  view={question.view}
                  choice={question.choice}
                  isDisabled={isOptionDisabled(option.value)}
                  variant={option.variant || 'body-sm-min-reg'}
                  ariaLabel={option.ariaLabel || `Input field: ${option.label}`}
                />
              );
            })}
            {/* Spacer to push input to top and fill remaining space */}
            <Box flex="1" />
        </VStack>
      );
    }

    return null;
  };

  return (
    <Flex
      direction="column"
      height="100%"
      minHeight="100%"
      justify="space-between"
    >
      {/* Main Content Container */}
      <Box flex="1" display="flex" flexDirection="column">
        {/* Question Title - NEW FEATURE */}
        {question.title && (
          <Text
            style={{
              fontSize: `var(--${question.titleVariant || 'headline-md-max'}-font-size)`,
              fontWeight: `var(--${question.titleVariant || 'headline-md-max'}-font-weight)`,
              color: `var(--${question.titleVariant || 'headline-md-max'}-color, var(--sutter-sync-purple))`,
            }}
            mt={2}
            mb={4}
          >
            {question.title}
          </Text>
        )}
        
        {/* Question Prompt */}
        {question.prompt && (
          <Text
            style={{
              fontSize: `var(--${question.variant}-font-size)`,
              fontWeight: `var(--${question.variant}-font-weight)`,
              color: `var(--${question.variant}-color, var(--sutter-sync-purple))`,
            }}
            mt={question.marginTop !== undefined ? question.marginTop : (question.title ? 0 : 2)}
            mb={question.marginBottom !== undefined ? question.marginBottom : 8}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.prompt) }}
          />
        )}
         {/* Helper Text */}
         {question.helperText && (
          <Text
            style={{
              fontSize: `var(--${question.helperTextVariant}-font-size)`,
              fontWeight: `var(--${question.helperTextVariant}-font-weight)`,
              color: `var(--${question.helperTextVariant}-color, var(--sutter-sync-purple))`,
            }}
            mt={-2}
            mb={2}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.helperText) }}
          />
          )}
        
        {/* Rating Scale */}
        {question.ratingScale && Array.isArray(question.ratingScale) && question.ratingScale.length === 2 && (
          <Flex justify="space-between" mb={4}>
            {question.ratingScale.map((label, index) => {
              const variant = question.ratingScaleVariant || 'body-xs-min-bold';
              return (
                <Text
                  key={index}
                  textAlign={index === 0 ? 'left' : 'right'}
                  style={{
                    fontSize: `var(--${variant}-font-size, 14px)`,
                    fontWeight: `var(--${variant}-font-weight, 700)`,
                    fontFamily: `var(--${variant}-font-family, Roboto, sans-serif)`,
                    lineHeight: `var(--${variant}-line-height, 16px)`,
                    letterSpacing: `var(--${variant}-letter-spacing, 0.5px)`,
                    color: `var(--${variant}-color, var(--color-dusky-teal, #203433))`,
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(label) }}
                >
                </Text>
              );
            })}
          </Flex>
        )}
         
         {/* List Content - NEW FEATURE */}
         {question.list && question.list.items && question.list.items.length > 0 && (
           <Box mb={4}>
             <ListComponent
               items={question.list.items}
               styleType={question.list.styleType || 'disc'}
               variant={question.list.variant || 'body-sm-min-reg'}
               icon={question.list.icon || {}}
               image={question.list.image}
               markerColor={question.list.markerColor}
               marginTop={question.list.marginTop}
               marginBottom={question.list.marginBottom}
               itemColor={question.list.itemColor}
               startNumber={question.list.startNumber || 1}
             />
           </Box>
         )}
         
         {/* Select All/Deselect All Button - positioned between prompt and options */}
         {!readOnly && question.button?.some(btn => btn.action === 'select-all') && (
           <Box mb={4} display="flex" justifyContent="flex-end">
             {question.button
               .filter(btn => btn.action === 'select-all')
               .map((btn, btnIndex) => {
                 const buttonLabel = areAllVisibleOptionsSelected ? (btn.deselectLabel || 'Deselect all') : (btn.label || 'Select all');
                 const buttonVariant = buttonLabel.toLowerCase().includes('deselect all')
                   ? 'deselect-all'
                   : buttonLabel.toLowerCase().includes('select all')
                   ? 'select-all'
                   : (btn.variant || 'outline-half');
                 const buttonAriaLabel = areAllVisibleOptionsSelected ? (btn.deselectAriaLabel || 'Deselect all options') : (btn.ariaLabel || 'Select all options');
                 const buttonTooltip = areAllVisibleOptionsSelected ? (btn.deselectTooltip || 'Click to deselect all conditions') : (btn.tooltip || 'Click to select all conditions');

                 return (
                   <ButtonComponent
                     key={`select-all-${btnIndex}`}
                     label={buttonLabel}
                     variant={buttonVariant}
                     isDisabled={false} // This button should always be enabled
                     ariaLabel={buttonAriaLabel}
                     tooltip={buttonTooltip}
                     onClick={handleSelectAll}
                     marginBottom={'0'}
                     isTouchDevice={isTouchDevice}
                     isMouseDevice={isMouseDevice}
                   />
                 );
               })}
           </Box>
         )}
         
         {/* Render Choice Group */}
           {renderChoiceGroup()}
      </Box>

      {/* Spacer to push buttons to bottom */}
      <SpacerComponent />

      {/* Bottom Buttons Container */}
      {!readOnly && question.button?.length > 0 && (
        <Box pt={question.buttonPaddingTop !== undefined ? question.buttonPaddingTop : 4}>
          {question.button
            .filter(btn => btn.action !== 'select-all') // Exclude select-all from bottom buttons
            .map((btn, index) => {
            const isBackButton = btn.action === 'back';
            const isNoThanksButton = btn.label?.toLowerCase().includes('no thanks');
            const buttonValue = formData?.[question.id]?.value;
            const selectedCount = Array.isArray(buttonValue)
              ? buttonValue.filter(v => v !== '' && v !== null && v !== undefined).length
              : (buttonValue !== undefined && buttonValue !== null && buttonValue !== '' ? 1 : 0);
            const disableNoThanksWhenSelected = isNoThanksButton && selectedCount > 0;
            const isDisabled = isBackButton
              ? false
              : (disableNoThanksWhenSelected || (isButtonDisabled && !isNoThanksButton));

            // Dynamic label logic for action buttons based on number of selections
            let dynamicLabel = btn.label || 'Next';
             console.log("Dynamic label ", dynamicLabel, "Button value", buttonValue, "Form data", formData?.[question.id]);
            if (!isNoThanksButton && !isBackButton && btn.label?.toLowerCase().includes('add')) {
              if (selectedCount === 0) {
                dynamicLabel = 'Add';
              } else if (selectedCount === 1) {
                dynamicLabel = 'Add';
              } else if (selectedCount === 2) {
                dynamicLabel = 'Add both';
              } else if (selectedCount > 2) {
                dynamicLabel = 'Add all';
              }
            } else if (!isNoThanksButton && !isBackButton && btn.label?.toLowerCase().includes('skip')) {
             console.log("SELECTED COUNT", selectedCount);
              dynamicLabel = selectedCount > 0 ? 'Next' : 'Skip';
            }

            // Determine the variant logic
            let variant = btn.variant || 'solid';
            
            // If button is disabled, check for 'half' in variant and use 'disabled-half'
            if (!isBackButton && isDisabled) {
              variant = btn.variant?.includes('half') ? 'disabled-half' : 'disabled';
            }

            return (
              <ButtonComponent
                key={index}
                label={dynamicLabel}
                variant={variant}
                isDisabled={isDisabled}
                ariaLabel={btn.ariaLabel || ''}
                tooltip={btn.tooltip || ''}
                onClick={() => handleButtonAction(btn.action, btn)}
                marginBottom={'5px'}
                isTouchDevice={isTouchDevice}
                isMouseDevice={isMouseDevice}
              />
            );
          })}
        </Box>
      )}
    </Flex>
  );
};

export default QuestionnaireComponent;